import { memo, useEffect, useState } from "react";
import { PrismBadge, PrismButton, PrismCard } from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faPen, faVideo, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import clearLogoSrc from "../../assets/images/clear-logo.svg";
import type { Project, TeamMember } from "./projects.utils";
import { groupIntoTriads } from "./projects.utils";
import ProjectEditorModal from "./ProjectEditorModal";
import styles from "./ProjectCard.module.css";

interface ProjectCardProps {
  data: Project & {
    logo?: string;
    video_url?: string;
    video_stream_url?: string;
    spark_url?: string;
    thanks?: string;
  };
  editable?: boolean;
  collapsible?: boolean;
  collapsed?: boolean;
  open?: boolean;
  live?: boolean;
  /**
   * Whether this event has a Teams stream at all. In-person events share their projects
   * with the virtual fair, so `live` alone would advertise a meeting that does not exist.
   * Suppresses only the Join Live button; the post-fair "Watch" recording link is
   * unaffected.
   */
  joinLiveEnabled?: boolean;
  inlineActions?: boolean;
}

const logoSrc = (p: ProjectCardProps["data"]): string => {
  if (p.logo && p.logo === "GENERIC") return clearLogoSrc;
  if (p.logo && p.logo.indexOf("api/files") < 0) {
    return `/api/files/images/project-fair/${p.year}/logos/${p.logo}`;
  }
  if (p.logo && !p.logo.startsWith("/")) return `/${p.logo}`;
  return p.logo || clearLogoSrc;
};

const menteeNames = (team?: TeamMember[]): string =>
  (team ?? [])
    .filter((t) => (t.role ?? "").toLowerCase() === "mentee")
    .map((t) => t.name)
    .join(" & ");

const ProjectCard = ({
  data: p,
  editable = false,
  collapsible = false,
  collapsed = false,
  open,
  live = false,
  joinLiveEnabled = true,
  inlineActions = false,
}: ProjectCardProps) => {
  const canCollapse = collapsed || collapsible;
  const [project, setProject] = useState(p);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    setProject(p);
  }, [p]);

  useEffect(() => {
    // Keep the expanded state in sync with parent-driven props. An explicit `open`
    // (ProjectFair "Collapse All") wins; otherwise the `collapsed` prop drives it, so
    // toggling Classic<->Compact actually expands/collapses already-mounted cards.
    if (typeof open === "boolean") setExpanded(open);
    else setExpanded(!collapsed);
  }, [open, collapsed]);

  const [expanded, setExpanded] = useState<boolean>(collapsed ? false : open ?? true);

  const tags = project.tags ?? [];
  const teamMembers = project.team?.team ?? [];
  const sponsors = project.exec_sponsor ?? [];
  const { triads, unassigned } = groupIntoTriads(teamMembers);

  // Action buttons (Watch/Join, Comment, Edit) render in a rail pinned to the bottom of
  // the card so they line up across a row regardless of how much content each card holds.
  // They live in the default slot (a native div we own) — NOT the PrismCard `actions`
  // footer slot — because the card's footer sits three shadow-DOM levels down
  // (.card > .stacked > .content > .footer) where only `.card` is reachable as
  // ::part(inner); the intermediate wrappers never stretch, so the footer can't be pinned
  // from outside. The rail is absolutely positioned against ::part(inner) (which is
  // position:relative and full height) instead. In the compact view a collapsed card hides
  // the rail until it is revealed.
  // In the timeline popover the card is a fixed, narrow width, so the action rail flows
  // inline (not the absolute bottom-pinned rail used to align a grid row) and the buttons
  // shrink so all three fit on one line without wrapping or overlapping short content.
  const actionsHidden = collapsed && !expanded;
  const actionSize = inlineActions ? "xs" : "sm";
  const actionSpacing = inlineActions ? "compact" : "standard";

  const videoButton = () => {
    if (live && joinLiveEnabled && project.video_url) {
      return (
        <PrismButton key="video" tone="secondary" size={actionSize} spacing={actionSpacing} href={project.video_url} target="_blank" rel="noopener noreferrer">
          <FontAwesomeIcon icon={faVideo} /> Join Live
        </PrismButton>
      );
    }
    if (!live && project.video_stream_url) {
      return (
        <PrismButton key="video" tone="secondary" size={actionSize} spacing={actionSpacing} href={project.video_stream_url} target="_blank" rel="noopener noreferrer">
          <FontAwesomeIcon icon={faVideo} /> Watch
        </PrismButton>
      );
    }
    return null;
  };

  const sparkButton = () =>
    project.spark_url ? (
      <PrismButton key="spark" tone="secondary" size={actionSize} spacing={actionSpacing} href={project.spark_url} target="_blank" rel="noopener noreferrer">
        <FontAwesomeIcon icon={faComment} /> Comment
      </PrismButton>
    ) : null;

  const editButton = () =>
    editable ? (
      <PrismButton key="edit" tone="secondary" size={actionSize} spacing={actionSpacing} onPrismClick={() => setEditorOpen(true)}>
        <FontAwesomeIcon icon={faPen} /> Edit
      </PrismButton>
    ) : null;

  const actionButtons = [videoButton(), sparkButton(), editButton()].filter(Boolean);
  const showActions = actionButtons.length > 0 && !actionsHidden;

  return (
    <>
      <PrismCard className={styles.card} display="static" headline={project.title}>
        <img slot="image" className={styles.logo} src={logoSrc(project)} alt={`${project.title} Logo`} />

        <div
          className={
            showActions && !inlineActions ? `${styles.body} ${styles.bodyWithRail}` : styles.body
          }
        >
          <div className={styles.subtitle}>
            {menteeNames(teamMembers)}
            <span className={styles.location}>
              {project.Chapter?.location || ""} {project.year}
            </span>
          </div>

          {project.active === false && (
            <p className={styles.hiddenState}>
              <PrismBadge tone="caution" size="sm">Hidden</PrismBadge>
            </p>
          )}

          {tags.length > 0 && (
            <p className={styles.tags}>
              {tags.map((t, index) => (
                <PrismBadge key={String(t.tag?.id ?? `${index}-${t.tag?.name ?? "tag"}`)} tone="info" size="sm">
                  {t.tag?.name ?? ""}
                </PrismBadge>
              ))}
            </p>
          )}

          {expanded && (
            <div className={styles.details}>
              {project.description && (
                <section>
                  <h5>Description</h5>
                  <p className={styles.description}>{project.description}</p>
                </section>
              )}

              {(teamMembers.length > 0 || sponsors.length > 0) && (
                <section>
                  <h5>Team Members</h5>
                  {triads.map(({ number, slots }) => {
                    // Only slots that are actually filled. Naming an empty role is the right
                    // call in the officer-facing import wizard, where "no column found" is
                    // exactly what needs reporting — but /projects is public, and telling a
                    // visitor a mentor was "not recorded" airs an internal data-quality note
                    // about a real person's project.
                    const filled = slots.filter(({ member }) => member);
                    if (!filled.length) return null;
                    return (
                      <div key={`t-${number}`} className={styles.triad}>
                        {triads.length > 1 && <div className={styles.triadLabel}>Triad {number}</div>}
                        <ul className={styles.memberList}>
                          {filled.map(({ role, member }) => (
                            <li key={`${number}-${role}`}>
                              <b>{member!.name}</b> / {role}
                              {member!.title ? <div className={styles.muted}>{member!.title}</div> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}

                  {unassigned.length > 0 && (
                    <ul className={styles.memberList}>
                      {unassigned.map((t, i) => (
                        <li key={`u-${i}`}>
                          <b>{t.name}</b> / {t.role}
                          {t.title ? <div className={styles.muted}>{t.title}</div> : null}
                        </li>
                      ))}
                    </ul>
                  )}

                  {sponsors.length > 0 && (
                    <div className={styles.triad}>
                      <div className={styles.triadLabel}>
                        Executive {sponsors.length > 1 ? "Sponsors" : "Sponsor"}
                      </div>
                      <ul className={styles.memberList}>
                        {sponsors.map((e, i) => (
                          <li key={`s-${i}`}>
                            <b>{e.name}</b>
                            {e.title ? <div className={styles.muted}>{e.title}</div> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}

              {project.thanks && (
                <section className={styles.thanks}>
                  {/* The roster above ends with the executive sponsors, so without a divider
                      the thanks read as being *from* the sponsors rather than from the team. */}
                  <hr className={styles.thanksRule} />
                  <h5>Special Thanks</h5>
                  <p>{project.thanks}</p>
                </section>
              )}            </div>
          )}

          {canCollapse && (
            <PrismButton
              className={styles.toggle}
              tone="secondary"
              display="link"
              size="sm"
              setAriaExpanded={expanded}
              onPrismClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Hide" : "Show"} Details <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
            </PrismButton>
          )}

          {showActions && (
            <div className={inlineActions ? styles.actionRailInline : styles.actionRail}>
              {actionButtons}
            </div>
          )}
        </div>
      </PrismCard>

      <ProjectEditorModal
        open={editorOpen}
        mode="edit"
        project={project}
        onClose={() => setEditorOpen(false)}
        onSaved={(saved) => {
          setProject((previous) => ({ ...previous, ...saved }));
          setEditorOpen(false);
        }}
      />
    </>
  );
};

// Memoized: the Projects/ProjectFair grids can render hundreds of cards, and every
// filter/search/sort keystroke updates parent state. Without memo, all visible cards
// re-render on each change even though their props are unchanged. `data` object identity
// is preserved through applyPipeline (filter/sort return the same object refs), so the
// default shallow prop compare is correct here.
export default memo(ProjectCard);
